import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Chip, Tooltip } from '@mui/material';
import { AccountTree, ChevronRight, TipsAndUpdates } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import projectAnalyticsService, { ProjectCommandCenter as CommandCenterData } from '../../services/projectAnalyticsService';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import { ErrorState, StatusDot, StatusTone } from '../../components/common/pageKit';
import { RouterLink } from '../../utils/linkNav';

// ---- pure formatters ----
const isNum = (v: any) => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Math.round(Number(v))}%` : '—');

const HEALTH_TONE: Record<string, StatusTone> = {
  ON_TRACK: 'success',
  WATCH: 'info',
  AT_RISK: 'warning',
  CRITICAL: 'error',
};

const HEALTH_LABEL: Record<string, string> = {
  ON_TRACK: 'On track',
  WATCH: 'Watch',
  AT_RISK: 'At risk',
  CRITICAL: 'Critical',
};

const SEVERITY_RANK: Record<string, number> = { CRITICAL: 0, AT_RISK: 1, WATCH: 2, ON_TRACK: 3 };

/** Pick the signal that most explains the overall band — the worst one on file. */
const topSignal = (signals: Array<{ dimension: string; state: string; detail: string }> = []) => {
  if (!signals.length) return null;
  return [...signals].sort((a, b) => (SEVERITY_RANK[a.state] ?? 9) - (SEVERITY_RANK[b.state] ?? 9))[0];
};

const attentionTone = (severity: string): StatusTone => {
  const s = (severity || '').toLowerCase();
  if (s === 'critical') return 'error';
  if (s === 'warning') return 'warning';
  return 'info';
};

const insightLabel = (i: { label: string } | string): string => (typeof i === 'string' ? i : i?.label || '');

const ProjectCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const T = useChartTokens();

  const [data, setData] = useState<CommandCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    projectAnalyticsService
      .commandCenter()
      .then((res) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load the project command center.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = data?.kpis || {};
  const projects = useMemo(() => data?.projects || [], [data]);
  const attention = data?.attention || [];
  const insights = data?.insights || [];

  const grossMargin = useMemo(() => {
    const revenue = Number(kpis.totalContractValue) || 0;
    const actual = Number(kpis.actualCost) || 0;
    if (!revenue) return null;
    return revenue - actual;
  }, [kpis]);

  const kpiDefs = useMemo(
    () => [
      { key: 'activeProjects', label: 'Active Projects', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.text },
      { key: 'onTrack', label: 'On Track', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.good },
      { key: 'delayed', label: 'Watch', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.cyan },
      { key: 'atRisk', label: 'At Risk', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.warn },
      { key: 'critical', label: 'Critical', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.bad },
      { key: 'totalContractValue', label: 'Contract Value', fmt: fmtMoney, color: C.text },
      { key: 'totalBudget', label: 'Budget', fmt: fmtMoney, color: C.text },
      { key: 'actualCost', label: 'Actual Cost', fmt: fmtMoney, color: C.warn },
      { key: 'committedCost', label: 'Committed', fmt: fmtMoney, color: C.purple },
      { key: 'billedAmount', label: 'Billed', fmt: fmtMoney, color: C.accent },
      { key: 'collectedAmount', label: 'Collected', fmt: fmtMoney, color: C.good },
      { key: 'unbilledAmount', label: 'Unbilled', fmt: fmtMoney, color: C.cyan },
      { key: '__grossMargin', label: 'Gross Margin', fmt: () => fmtMoney(grossMargin), color: grossMargin != null && grossMargin < 0 ? C.bad : C.good },
      { key: 'avgProgress', label: 'Avg Progress', fmt: fmtPct, color: C.text },
      { key: 'overdueTasks', label: 'Overdue Tasks', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: Number(kpis.overdueTasks) > 0 ? C.bad : C.text },
      { key: 'upcomingMilestones', label: 'Upcoming Milestones', fmt: (v: any) => (isNum(v) ? String(v) : '—'), color: C.text },
    ],
    [C, kpis, grossMargin]
  );

  const budgetVsActualData = useMemo(
    () =>
      [...projects]
        .sort((a, b) => (Number(b.budget) || 0) - (Number(a.budget) || 0))
        .slice(0, 8)
        .map((p) => ({ name: p.code || p.name, fullName: p.name, budget: Number(p.budget) || 0, actual: Number(p.actualCost) || 0 })),
    [projects]
  );

  const healthDonutData = useMemo(
    () =>
      [
        { key: 'ON_TRACK', name: 'On track', value: Number(kpis.onTrack) || 0 },
        { key: 'WATCH', name: 'Watch', value: Number(kpis.delayed) || 0 },
        { key: 'AT_RISK', name: 'At risk', value: Number(kpis.atRisk) || 0 },
        { key: 'CRITICAL', name: 'Critical', value: Number(kpis.critical) || 0 },
      ].filter((d) => d.value > 0),
    [kpis]
  );

  const donutColor: Record<string, string> = {
    ON_TRACK: T.status.good,
    WATCH: T.status.warning,
    AT_RISK: T.status.serious,
    CRITICAL: T.status.critical,
  };

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, minWidth: 0 }}>
      <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{
          fontWeight: 800,
          fontSize: { xs: 17, sm: 19, md: 20, lg: 22 },
          lineHeight: 1.15,
          color: color || C.text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${C.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AccountTree sx={{ color: C.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Project Command Center</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Which projects are healthy, delayed, over budget?</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/projects/portfolio')} sx={{ color: C.text, borderColor: C.line }}>
            Portfolio
          </Button>
          <Button variant="outlined" onClick={() => navigate('/projects/my-day')} sx={{ color: C.text, borderColor: C.line }}>
            My Day
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          {/* Exec KPI band */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(150px, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {kpiDefs.map((k) => (
              <Metric key={k.key} label={k.label} value={k.fmt(kpis[k.key])} color={k.color} />
            ))}
          </Box>

          {/* Charts row */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
            <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Budget vs Actual · top projects
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                {budgetVsActualData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={budgetVsActualData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridProps(T)} />
                      <XAxis dataKey="name" {...axisProps(T)} />
                      <YAxis {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} width={70} />
                      <RTooltip
                        {...tooltipStyle(T)}
                        formatter={(v: any, name: any) => [formatIndianNumber(Number(v)), name]}
                        labelFormatter={(_l: any, payload: any) => payload?.[0]?.payload?.fullName || _l}
                      />
                      <Legend wrapperStyle={{ fontSize: 12, color: T.textSecondary }} />
                      <Bar dataKey="budget" name="Budget" fill={T.series(0)} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="actual" name="Actual" fill={T.series(1)} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: C.sub, p: 2 }}>No project cost data available.</Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Health Distribution
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                {healthDonutData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={healthDonutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        label={(p: any) => `${p.name} (${p.value})`}
                        labelLine={false}
                      >
                        {healthDonutData.map((d) => (
                          <Cell key={d.key} fill={donutColor[d.key]} />
                        ))}
                      </Pie>
                      <RTooltip {...tooltipStyle(T)} />
                      <Legend wrapperStyle={{ fontSize: 12, color: T.textSecondary }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: C.sub, p: 2 }}>No active projects to assess.</Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Projects health strip */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Projects · click to open workspace
            </Typography>
            <Stack spacing={1.25}>
              {projects.map((p: any) => {
                const band = p.health?.band || 'ON_TRACK';
                const sig = topSignal(p.health?.signals);
                const progress = Math.min(Math.max(Number(p.progressPercent) || 0, 0), 100);
                return (
                  <Box
                    key={p.id}
                    component={RouterLink}
                    to={`/projects/${p.id}/workspace`}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                      bgcolor: C.panel,
                      border: `1px solid ${band === 'CRITICAL' ? C.bad : C.line}`,
                      borderRadius: 2,
                      p: 2,
                      '&:hover': { borderColor: C.accent },
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5}>
                      <Box sx={{ minWidth: 220, flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: C.sub }} noWrap>
                          {p.customerName || 'No customer'}
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: C.sub }}>Progress</Typography>
                          <Typography sx={{ fontSize: 11, color: C.text, fontWeight: 700 }}>{progress}%</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: `${C.line}`,
                            '& .MuiLinearProgress-bar': { bgcolor: band === 'CRITICAL' || band === 'AT_RISK' ? C.warn : C.accent, borderRadius: 3 },
                          }}
                        />
                      </Box>

                      <Box sx={{ minWidth: 160, flexShrink: 0, textAlign: { xs: 'left', md: 'right' } }}>
                        <Typography sx={{ fontSize: 11, color: C.sub }}>Budget vs Actual</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {fmtMoney(p.budget)} <Box component="span" sx={{ color: C.sub, fontWeight: 400 }}>vs</Box>{' '}
                          <Box component="span" sx={{ color: Number(p.actualCost) > Number(p.budget) ? C.bad : C.text }}>
                            {fmtMoney(p.actualCost)}
                          </Box>
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 220, flexShrink: 0 }}>
                        <StatusDot label={HEALTH_LABEL[band] || band} tone={HEALTH_TONE[band] || 'neutral'} />
                        {sig && (
                          <Tooltip title={sig.detail}>
                            <Typography sx={{ fontSize: 11, color: C.sub, mt: 0.25 }} noWrap>
                              {sig.detail}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>

                      <ChevronRight sx={{ color: C.sub, flexShrink: 0, display: { xs: 'none', md: 'block' } }} />
                    </Stack>
                  </Box>
                );
              })}
              {!loading && !projects.length && <Typography sx={{ color: C.sub, fontStyle: 'italic' }}>No projects on file.</Typography>}
            </Stack>
          </Box>

          {/* Attention + Insights */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Attention Required
              </Typography>
              <Stack spacing={1}>
                {attention.map((a, i) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: C.panel,
                      border: `1px solid ${C.line}`,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <StatusDot label={a.severity ? a.severity.charAt(0).toUpperCase() + a.severity.slice(1) : '—'} tone={attentionTone(a.severity)} />
                    <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0 }} noWrap>
                      {a.label}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ChevronRight />}
                      disabled={!a.link}
                      onClick={() => a.link && navigate(a.link)}
                      sx={{ color: C.accent, flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  </Box>
                ))}
                {!loading && !attention.length && (
                  <Typography sx={{ color: C.sub, fontStyle: 'italic', py: 2 }}>Nothing needs attention.</Typography>
                )}
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Insights
              </Typography>
              <Stack spacing={1}>
                {insights.map((ins, i) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: C.panel,
                      border: `1px solid ${C.line}`,
                      borderRadius: 2,
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <TipsAndUpdates sx={{ fontSize: 16, color: C.cyan, flexShrink: 0, mt: 0.25 }} />
                    <Typography sx={{ fontSize: 12.5 }}>{insightLabel(ins)}</Typography>
                  </Box>
                ))}
                {!loading && !insights.length && (
                  <Typography sx={{ color: C.sub, fontStyle: 'italic', py: 2 }}>No insights yet.</Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProjectCommandCenter;
